import {
  MapContainer,
  TileLayer,
  Marker,
  Tooltip,
  GeoJSON,
} from "react-leaflet";
import {
  PlayerInfo,
  LocationInfo,
  Coordinates,
  LocationInfo_Type,
  TeamInfoResponse,
  GameMode,
} from "../api/copenhunt/v1/copenhunt_pb";
import "leaflet/dist/leaflet.css";
import css from "../App.module.css";
import { useEffect, useState } from "react";
import L from "leaflet";
import { Color, colorHexMap, Shape, shapeSvgMap } from "../utils";
import {
  Feature,
  FeatureCollection,
  GeoJsonObject,
  MultiPolygon,
  Polygon,
  Point,
} from "geojson";
import { center } from "@turf/turf";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import React from "react";

interface MapContainerProps {
  forPlayerId: string | null;
  forTeamId: string | null;
  playerInfos: PlayerInfo[];
  teamNumbers: Record<string, number>;
  teamColors: Record<string, Color>;
  objectiveShapes: Record<string, Shape>;
  locations: LocationInfo[];
  teamInfoById: { [key: string]: TeamInfoResponse };
  locationDelaySeconds: number;
  latestPingTimestamp?: number;
  pingIntervalSeconds: number;
  mode: GameMode;
}

const isPointInGeoJson = (p: Point, geoJson: GeoJsonObject): boolean => {
  if (geoJson.type === "Feature") {
    const feature = geoJson as Feature<Polygon | MultiPolygon>;
    console.assert(
      feature.geometry.type === "Polygon" ||
        feature.geometry.type === "MultiPolygon"
    );
    if (
      feature.geometry &&
      (feature.geometry.type === "Polygon" ||
        feature.geometry.type === "MultiPolygon")
    ) {
      return booleanPointInPolygon(p, feature);
    }
  } else if (geoJson.type === "FeatureCollection") {
    const featureCollection = geoJson as FeatureCollection<
      Polygon | MultiPolygon
    >;
    for (const feature of featureCollection.features) {
      console.assert(
        feature.geometry.type === "Polygon" ||
          feature.geometry.type === "MultiPolygon"
      );
      if (booleanPointInPolygon(p, feature)) {
        return true;
      }
    }
  }
  return false;
};

const GameMap: React.FC<MapContainerProps> = ({
  forPlayerId,
  forTeamId,
  playerInfos,
  teamColors,
  teamNumbers,
  objectiveShapes,
  locations,
  teamInfoById,
  locationDelaySeconds,
  latestPingTimestamp,
  pingIntervalSeconds,
  mode,
}) => {
  const [currentTime, setCurrentTime] = useState(Date.now() / 1000);

  useEffect(() => {
    setInterval(() => {
      setCurrentTime(Date.now() / 1000);
    }, 1000);
  }, []);

  const createCustomIcon = (
    color: Color,
    teamNumber: number,
    shape: Shape | null,
    isInactive: boolean,
    isSelf: boolean,
    opacity: number = 1
  ) => {
    const hex = isSelf
      ? "#4285F4"
      : colorHexMap[color as keyof typeof colorHexMap];
    const dangerIcon = isInactive
      ? `<polygon points="18,8 12,24 24,24" style="fill:red;stroke:white;stroke-width:2"/>
           <text x="18" y="21" font-size="10" text-anchor="middle" fill="white">!</text>`
      : "";

    const objectiveIcon = shape
      ? `<g transform="translate(24, 0) scale(0.4)" opacity="${opacity}">
           ${shapeSvgMap[shape].replace(
             "{color}",
             "#000000"
           )} <!-- Example: Red color -->
         </g>`
      : ""; // Only include the smaller icon if topRightShape is not null
    const circle = `<circle cx="18" cy="18" r="10" fill="${hex}" stroke="white" stroke-width="3" filter="url(#shadow)" opacity="${opacity}"/>`;

    const teamNumberIcon = `<text
    x="18" 
    y="22" 
    text-anchor="middle" 
    font-size="12" 
    fill="white" 
    font-weight="bold"
  >
  ${teamNumber}
  </text>`;

    const svgIcon = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 36" width="36" height="36">
      <defs>
        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="2.5" result="blur"/>
          <feOffset in="blur" dx="2" dy="2" result="offsetBlur"/>
          <feMerge>
            <feMergeNode in="offsetBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      ${circle}
      ${dangerIcon}
      ${teamNumberIcon}
      ${objectiveIcon}
    </svg>
  `;
    return L.divIcon({
      html: svgIcon,
      className: "",
      iconSize: [36, 36],
      iconAnchor: [18, 18],
      popupAnchor: [0, -18],
    });
  };

  const createLocationIcon = (shape: Shape | null) => {
    const svgIcon = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 36" width="36" height="36">
      <defs>
        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="2.5" result="blur"/>
          <feOffset in="blur" dx="2" dy="2" result="offsetBlur"/>
          <feMerge>
            <feMergeNode in="offsetBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      ${shape ? shapeSvgMap[shape].replace("{color}", "#000000") : ""}
    </svg>
  `;
    return L.divIcon({
      html: svgIcon,
      className: "",
      iconSize: [36, 36],
      iconAnchor: [18, 18],
      popupAnchor: [0, -18],
    });
  };

  let left = null;
  let right = null;
  let top = null;
  let bottom = null;
  for (const playerInfo of playerInfos) {
    const coordinates =
      playerInfo.coordinates.length > 0
        ? playerInfo.coordinates[playerInfo.coordinates.length - 1]
        : null;
    if (coordinates?.latitude === 0 && coordinates?.longitude === 0) continue;
    const lat = coordinates?.latitude;
    const long = coordinates?.longitude;
    if (lat === undefined || long === undefined) {
      continue;
    }
    if (left === null || left > lat) {
      left = lat;
    }
    if (right === null || right < lat) {
      right = lat;
    }
    if (top === null || top > long) {
      top = long;
    }
    if (bottom === null || bottom < long) {
      bottom = long;
    }
  }
  let left_top = [0.0, 0.0] as [number, number];
  let right_bottom = [0.0, 0.0] as [number, number];
  if (left !== null && top !== null) {
    left_top = [left, top];
  }
  if (right !== null && bottom !== null) {
    right_bottom = [right, bottom];
  }

  if (left_top[0] === 0 && left_top[1] === 0) {
    left_top = [55.67278969729665, 12.546423923100486];
  }
  if (right_bottom[0] === 0 && right_bottom[1] === 0) {
    right_bottom = [55.67278969729665, 12.546423923100486];
  }

  left_top[0] -= 0.01;
  left_top[1] -= 0.01;
  right_bottom[0] += 0.01;
  right_bottom[1] += 0.01;

  const getStyle = (type: LocationInfo_Type, locationId: string) => {
    switch (type) {
      case LocationInfo_Type.TREASURE:
        return {
          color: "blue", // line color
          weight: 4, // line thickness
          dashArray: "2, 8", // pattern: X px dash, Y px gap
          fillOpacity: 0.2, // fill opacity
          fill: true, // fill the polygon
        };
      case LocationInfo_Type.BANK:
        return { color: "green", weight: 4, fillOpacity: 0.2, fill: true };
      case LocationInfo_Type.HUNTING_GROUNDS:
        return { color: "orange", weight: 4, fillOpacity: 0.2, fill: true };
      default:
        return { color: "red" };
    }
  };

  let parsedLocations = locations
    .map((location) => {
      try {
        return {
          type: location.type,
          geoJson: JSON.parse(location.geoJson) as GeoJsonObject,
          id: location.id,
        };
      } catch (e) {
        console.error("Invalid GeoJSON format", e, location.geoJson);
        console.error(location.geoJson);
        return null;
      }
    })
    .filter(
      (
        location
      ): location is {
        type: LocationInfo_Type;
        geoJson: GeoJsonObject;
        id: string;
      } => location !== null
    );

  return (
    <div className="w-full bg-gray-100 h-[600px] md:h-[550px]">
      <MapContainer
        bounds={[left_top, right_bottom]}
        scrollWheelZoom={false}
        className={css.mapContainer}
        style={{ zIndex: 0 }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {
          // Show each player's last 10 coordinates on the map
          // with the last one being the main marker
          // and the rest being smaller markers
          playerInfos.map((playerInfo) => {
            if (playerInfo.coordinates.length === 0) {
              return null;
            }

            const latestCoord =
              playerInfo.coordinates[playerInfo.coordinates.length - 1];

            const delayCutoff = currentTime - locationDelaySeconds;
            const effectiveCutoff = Math.max(
              delayCutoff,
              latestPingTimestamp || 0
            );

            const teamId = playerInfo.teamId;
            const teamInfo = teamInfoById[teamId];
            if (!teamInfo) {
              return null;
            }
            const teamNumber = teamNumbers[teamId];
            const teamColor = teamColors[teamId];

            const isHunter = teamInfo.isHunter;

            const isHunterInsideHuntingGround = (coord: Coordinates) => {
              if (!isHunter) {
                return false;
              }

              const huntingGrounds = parsedLocations.filter(
                (l) => l.type === LocationInfo_Type.HUNTING_GROUNDS
              );
              const playerPoint: Point = {
                type: "Point",
                coordinates: [coord.longitude, coord.latitude],
              };
              for (const hg of huntingGrounds) {
                if (isPointInGeoJson(playerPoint, hg.geoJson)) {
                  return true;
                }
              }

              return false;
            };

            let delayedCoord = [...playerInfo.coordinates]
              .reverse()
              .find(
                (c) =>
                  c.timestamp < effectiveCutoff ||
                  isHunterInsideHuntingGround(c)
              );

            if (!delayedCoord) {
              // We don't have a coordinate old enough to be the delayed coordinate;
              // so we use the oldest coordinate we have.
              delayedCoord = playerInfo.coordinates[0];
            }

            const delayedCoordAgeSeconds = Math.round(
              currentTime - delayedCoord.timestamp
            );

            // const maxAge = Math.min(
            //   locationDelaySeconds,
            //   pingIntervalSeconds || locationDelaySeconds
            // );
            // const ageRatio = Math.min(delayedCoordAgeSeconds, maxAge) / maxAge;
            // const opacity = 0.8 - ageRatio * 0.6; // scales from 0.8 down to 0.2
            const opacity = 0.5;

            const objectiveShape = objectiveShapes[teamInfo.objectiveId];

            const markers = [];

            const isInactive =
              currentTime - latestCoord.timestamp > locationDelaySeconds;

            // The lastest coordinate is visible if...
            // A) we are in the admin view, or...
            // B) we're looking at a teammate, or...
            // C) the player is a hunter inside a hunting ground.
            const showLatestCoord =
              !forTeamId ||
              forTeamId === playerInfo.teamId ||
              isHunterInsideHuntingGround(latestCoord);
            const showDelayedCoord =
              !showLatestCoord || delayedCoord !== latestCoord;

            if (showLatestCoord) {
              markers.push(
                <Marker
                  position={[latestCoord.latitude, latestCoord.longitude]}
                  icon={createCustomIcon(
                    teamColor,
                    teamNumber,
                    objectiveShape,
                    isInactive,
                    forPlayerId === playerInfo.playerId
                  )}
                  key={`${playerInfo.playerId}-latest`}
                >
                  <Tooltip>
                    {playerInfo.name}
                    <br />
                    Team: {teamInfo.name}
                    <br />
                    {isInactive ? "Inactive" : "Active"}
                  </Tooltip>
                </Marker>
              );
            }

            if (showDelayedCoord) {
              markers.push(
                <Marker
                  position={[delayedCoord.latitude, delayedCoord.longitude]}
                  icon={createCustomIcon(
                    teamColor,
                    teamNumber,
                    objectiveShape,
                    isInactive,
                    forPlayerId === playerInfo.playerId,
                    opacity
                  )}
                  key={`${playerInfo.playerId}-delayed`}
                >
                  <Tooltip>
                    {playerInfo.name} ({delayedCoordAgeSeconds} seconds ago)
                    <br />
                    Team: {teamInfo.name}
                  </Tooltip>
                </Marker>
              );
            }

            return markers;
          })
        }
        {parsedLocations.map((location) => {
          if (location === undefined) {
            console.error("Invalid location", location);
            return null;
          }
          const feature = location.geoJson as Feature;
          if (feature === undefined) {
            console.error("Invalid feature", feature);
            return null;
          }
          if (feature.geometry === undefined) {
            console.error("Invalid feature geometry", feature);
            return null;
          }
          if (feature.geometry.type === "Point") {
            if (
              location.type === LocationInfo_Type.BANK ||
              location.type === LocationInfo_Type.HUNTING_GROUNDS
            ) {
              return null;
            }
            const coordinates = feature.geometry.coordinates;
            return (
              <Marker
                key={location.id}
                position={[coordinates[1], coordinates[0]]}
                icon={createLocationIcon(objectiveShapes[location.id])}
              >
                <Tooltip>{location.id}</Tooltip>
              </Marker>
            );
          } else {
            const featureCollection = {
              type: "FeatureCollection" as const,
              features: [feature],
            };
            const centerPoint = center(featureCollection);
            const [long, lat] = centerPoint.geometry.coordinates;

            return (
              <React.Fragment key={location.id}>
                <GeoJSON
                  data={location.geoJson as FeatureCollection}
                  style={getStyle(location.type, location.id)}
                />
                {location.type !== LocationInfo_Type.BANK &&
                  location.type !== LocationInfo_Type.HUNTING_GROUNDS && (
                    <Marker
                      position={[lat, long]}
                      icon={createLocationIcon(objectiveShapes[location.id])}
                    >
                      <Tooltip>{location.id}</Tooltip>
                    </Marker>
                  )}
              </React.Fragment>
            );
          }
        })}
      </MapContainer>
    </div>
  );
};

export default GameMap;
